
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';

export default {
  data() {
    return {
      map: {},
      properties: null,
      cityGeoJSON: null,
      hoveredPlaceId: null,
      boundsAsString: '',
      mouseIsOverPopup: false,
      popupPropertiesCount: null,
      popupCurrentProperty: null,
      loading: false,
      filters: {},
    };
  },
  computed: {},
  props: {
    city: Object,
    selected: String,
  },
  async created() {},
  updated() {},
  async mounted() {
    this.createMap();
    await this.getProperties();
    await this.getCityArea();
    this.addDataLayer();
    this.fitBoundsToCity();
  },
  methods: {
    createMap() {
      mapboxgl.accessToken = this.$config.mapboxToken;
      this.map = new mapboxgl.Map({
        container: 'map',
        style: 'mapbox://styles/mapbox/streets-v11',
        zoom: 11,
        minZoom: 11,
        center: [this.city.longitude, this.city.latitude],
      });

      this.map.addControl(new mapboxgl.NavigationControl());
      this.map.dragRotate.disable();
      this.map.touchZoomRotate.disableRotation();

      this.map.on('moveend', () => {
        this.updateDataSource();
      });

      this.boundsAsAString = this.getBoundsAsString();
      const geocoder = new MapboxGeocoder({
        accessToken: this.$config.mapboxToken,
        mapboxgl: mapboxgl,
        countries: 'fr',
        placeholder: 'Entrez une adresse',
      });

      document
        .getElementById('dvf-search')
        .appendChild(geocoder.onAdd(this.map));

      geocoder.on('result', (e) => {
        document.getElementById('dvf-title').style.display = 'none';
      });
    },
    getBoundsAsString() {
      return this.map.getBounds().toArray().toString();
    },
    getProperties: async function () {
      const filtersAsQueryString = Object.keys(this.filters)
        .map((key) => key + '=' + this.filters[key])
        .join('&');
      const response = await fetch(
        this.$config.baseApiURL +
          '/properties/' +
          (filtersAsQueryString.length ? 'filter/' : '') +
          '?geojson=1&bounds=' +
          this.boundsAsAString +
          (filtersAsQueryString.length ? '&' + filtersAsQueryString : '')
      );
      const props = await response.json();
      this.properties = props.features;
    },
    getCityArea: async function () {
      const response = await fetch(
        this.$config.baseApiURL + '/cities/' + this.city.slug + '/geojson'
      );
      const jsonResponse = await response.json();
      this.cityGeoJSON = jsonResponse[0];
    },
    fitBoundsToCity: function () {
      const bounds = this.getPolygonBoundingBox(this.cityGeoJSON.coordinates);
      this.map.fitBounds(bounds, {
        padding: 20,
      });
    },

    getPolygonBoundingBox: function (coordinates) {
      // bounds [xMin, yMin][xMax, yMax]
      let bounds = [[], []];
      let polygon;
      let latitude;
      let longitude;

      for (let i = 0; i < coordinates.length; i++) {
        if (coordinates.length === 1) {
          // Polygon coordinates[0][nodes]
          polygon = coordinates[0];
        } else {
          // Polygon coordinates[poly][0][nodes]
          polygon = coordinates[i][0];
        }

        for (let j = 0; j < polygon.length; j++) {
          longitude = polygon[j][0];
          latitude = polygon[j][1];

          bounds[0][0] = bounds[0][0] < longitude ? bounds[0][0] : longitude;
          bounds[1][0] = bounds[1][0] > longitude ? bounds[1][0] : longitude;
          bounds[0][1] = bounds[0][1] < latitude ? bounds[0][1] : latitude;
          bounds[1][1] = bounds[1][1] > latitude ? bounds[1][1] : latitude;
        }
      }
      return bounds;
    },

    addDataLayer: function () {
      this.map.addSource('city', {
        type: 'geojson',
        data: { type: 'Feature', geometry: this.cityGeoJSON },
      });

      this.map.addSource('places', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: this.properties },
      });

      this.map.addLayer({
        id: 'city',
        type: 'line',
        source: 'city',
        paint: {
          'line-color': 'blue',
          'line-width': 3,
        },
      });

      this.map.addLayer({
        id: 'places',
        type: 'circle',
        source: 'places',
        paint: {
          'circle-color': [
            'case',
            ['==', ['feature-state', 'hover'], true],
            'blue',
            ['==', ['feature-state', 'hover'], false],
            'blue',
            'blue',
          ],
          'circle-stroke-color': [
            'case',
            ['==', ['feature-state', 'hover'], true],
            'blue',
            ['==', ['feature-state', 'hover'], false],
            'white',
            'white',
          ],
          'circle-stroke-width': [
            'case',
            ['==', ['feature-state', 'hover'], true],
            20,
            ['==', ['feature-state', 'hover'], false],
            3,
            3,
          ],
          'circle-stroke-opacity': [
            'case',
            ['==', ['feature-state', 'hover'], true],
            0.2,
            ['==', ['feature-state', 'hover'], false],
            1,
            1,
          ],
          'circle-radius': [
            'case',
            ['==', ['feature-state', 'hover'], true],
            5,
            ['==', ['feature-state', 'hover'], false],
            5,
            5,
          ],
          'circle-opacity': [
            'case',
            ['==', ['feature-state', 'hover'], true],
            1,
            ['==', ['feature-state', 'hover'], false],
            1,
            1,
          ],
        },
      });

      this.fitBoundsToCity();

      // Create a popup, but don't add it to the map yet.
      const popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        maxWidth: 250,
        maxHeight: 150,
      });

      const showPopUp = (e) => {
        // Change the cursor style as a UI indicator.
        this.map.getCanvas().style.cursor = 'pointer';

        this.hoveredPlaceId = e.features[0].properties.id;

        this.map.setFeatureState(
          { source: 'places', id: this.hoveredPlaceId },
          { hover: true }
        );

        // Copy coordinates array.
        const coordinates = e.features[0].geometry.coordinates.slice();

        const properties = document.getElementsByClassName(
          e.features[0].properties.location_hash
        );

        this.popupPropertiesCount = properties.length;
        this.popupCurrentProperty = 0;

        const divElement = document.createElement('div');
        divElement.className = 'popup-content';

        let index = 0;
        for (const item of properties) {
          const itemElement = document.createElement('div');
          itemElement.innerHTML = item.innerHTML;
          index === 0
            ? itemElement.classList.add('current', 'property-item')
            : itemElement.classList.add('property-item');
          divElement.appendChild(itemElement);
          index++;
        }

        const getCounterValue = () => {
          return (
            this.popupCurrentProperty + 1 + ' / ' + this.popupPropertiesCount
          );
        };

        const setPrevNextPopupButtonState = () => {
          btnPrev.disabled = this.popupCurrentProperty < 1;
          btnNext.disabled =
            this.popupCurrentProperty + 1 === this.popupPropertiesCount;
        };

        const changeActivePropertyInPopup = (prevOrNext) => {
          divElement.children[this.popupCurrentProperty].classList.remove(
            'current'
          );
          prevOrNext === 'next'
            ? this.popupCurrentProperty++
            : this.popupCurrentProperty--;
          divElement.children[this.popupCurrentProperty].classList.add(
            'current'
          );
          counter.innerHTML = getCounterValue();
          setPrevNextPopupButtonState();
        };

        const blockPrevNextNav = document.createElement('div');
        blockPrevNextNav.className = 'prev-next';

        const btnPrev = document.createElement('button');
        btnPrev.innerHTML = 'Précédent';
        btnPrev.disabled = true;

        const counter = document.createElement('div');
        counter.innerHTML = getCounterValue();

        btnPrev.addEventListener('click', () =>
          changeActivePropertyInPopup('prev')
        );
        const btnNext = document.createElement('button');
        btnNext.innerHTML = 'Suivant';

        btnNext.addEventListener('click', () =>
          changeActivePropertyInPopup('next')
        );

        blockPrevNextNav.appendChild(btnPrev);
        blockPrevNextNav.appendChild(counter);
        blockPrevNextNav.appendChild(btnNext);

        divElement.addEventListener('mouseover', (e) => {
          this.mouseIsOverPopup = true;
        });

        divElement.addEventListener('mouseleave', (e) => {
          this.mouseIsOverPopup = false;
          popup.remove();
        });

        if (properties.length > 1) {
          divElement.appendChild(blockPrevNextNav);
        }

        // Ensure that if the map is zoomed out such that multiple
        // copies of the feature are visible, the popup appears
        // over the copy being pointed to.
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        // Populate the popup and set its coordinates
        // based on the feature found.
        popup.setLngLat(coordinates).setDOMContent(divElement).addTo(this.map);
      };

      this.map.on('mouseenter', 'places', (e) => {
        showPopUp(e);
      });

      this.map.on('click', 'places', (e) => {
        if (this.hoveredPlaceId > 0) {
          this.map.setFeatureState(
            { source: 'places', id: this.hoveredPlaceId },
            { hover: false }
          );
        }

        showPopUp(e);
      });

      this.map.on('mouseleave', 'places', (e) => {
        this.map.getCanvas().style.cursor = '';
        this.map.setFeatureState(
          { source: 'places', id: this.hoveredPlaceId },
          { hover: false }
        );

        setTimeout(() => {
          if (!this.mouseIsOverPopup) {
            popup.remove();
          }
        }, 200);
      });
    },

    updateDataSource: async function () {
      this.boundsAsAString = this.getBoundsAsString();
      this.loading = true;
      await this.getProperties();
      this.loading = false;
      this.map
        .getSource('places')
        .setData({ type: 'FeatureCollection', features: this.properties });
    },

    // getPropertiesInBounds: async function (bounds) {
    //   const boundsAsAString = bounds.toBBoxString();
    //   const filtersAsQueryString = Object.keys(this.filters)
    //     .map((key) => key + '=' + this.filters[key])
    //     .join('&');
    //   console.log('query');
    //   console.log(filtersAsQueryString);

    //   const response = await fetch(
    //     this.$config.baseApiURL + '/properties/?bounds=' + boundsAsAString
    //   );
    //   const props = await response.json();
    //   this.properties = props.data;
    // },
    highlightMarker: function (e) {
      if (typeof this.map.getLayer('places') !== 'undefined') {
        this.map.setFeatureState({ source: 'places', id: e }, { hover: true });
      }
    },
    resetMarker: function (e) {
      if (typeof this.map.getLayer('places') !== 'undefined') {
        this.map.setFeatureState({ source: 'places', id: e }, { hover: false });
      }
    },
    queryWithFilters: function () {
      console.log('ouppp');
    },
    test: function () {
      console.log('yeyyyyyyyy !!!');
    },
  },
  watch: {
    filters: {
      handler: function (val, oldVal) {
        //this.updateDataSource();
      },
      deep: true,
    },
  },
};
